import "../../css/GlobalStyles.css";
import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import CreatorModeWrapper from "../../components/CreatorDashboard/CreatorModeWrapper";
import { TabPanel } from "../../components/FullEventSubComponents/TabPanel";
import { SettingsPanel } from "../../components/EventDashboard/SettingsPanel";
import { AnalyticsPanel } from "../../components/EventDashboard/AnalyticsPanel";
import { useLoadTrackingLinksList } from "../../hooks/useLoadTrackingLinksList";
import { API } from "../../API";
import { getUserEventLinkTrackersRef } from "../../utils/FirebaseUtils";
import { onSnapshot, query, where } from "../../firebase";
import {
  AccessLevel,
  DefaultLinkTrackerName,
  Event,
  EventType,
  ThemeType,
  TicketV2,
} from "@markit/common.types";
import FullPostContent from "../../components/CreatorDashboard/Products/FullProduct/FullPostContent";
import { EventDashboardHeader } from "../../components/EventDashboard/EventDashboardHeader";
import { TrackingLinkModal } from "../../components/EventDashboard/DashboardModals/TrackingLinkModal";
import ShareEventModal from "../../components/EventDashboard/DashboardModals/ShareEventModal";
import { EventDashboardTabs } from "../../components/EventDashboard/EventDashboardTabs";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import FullProductContent from "../../components/CreatorDashboard/Products/FullProduct/FullProductContent";
import { OrderListPanel } from "../../components/EventDashboard/OrderListPanel";
import { useLiveUpdatingEventAnalytics } from "../../hooks/useLiveUpdatingEventAnalytics";
import { checkEventHasRevenue } from "../../utils/eventUtils/eventUtils";
import { isProductWithCheckout } from "@markit/common.utils";
import { TextsPanel } from "../../components/EventDashboard/TextsPanel";
import { useLiveUpdatingCampaign } from "../../hooks/useLiveHooks/useLiveUpdatingCampaign";
import LoadingScreen from "../LoadingScreen";
import { FormsPanel } from "../../components/EventDashboard/FormsPanel";
import { PromoCodePanel } from "../../components/EventDashboard/PromoCodePanel";

type ProductDashboardProps = {
  product: Event;
  userTicket: TicketV2 | undefined;
};

// Note (Peter): Has some similar aspects to EventDashboard but split it for simplicity for the Product Dashboard
// Would hopefully do a refactor in the future to combine the two, but for now there are too many things in EventDashboard that were event dependent
const ProductDashboard = (props: ProductDashboardProps) => {
  const { product, userTicket } = props;
  const { accountData } = useSelector(getAccountState).account;
  const [dashboardValue, setDashboardValue] = useState(0);
  const [numTotalLinkTrackers, setNumTotalLinkTrackers] = useState(0);
  const [linkTrackerId, setLinkTrackerId] = useState<string | undefined>(
    undefined
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isTrackingLinksView, setIsTrackingLinksView] = useState(false);
  const [trackingLinkModalVisible, setTrackingLinkModalVisible] =
    useState(false);
  const [trackingLinkModalText, setTrackingLinkModalText] = useState("");
  const [hasPaidRevenue, setHasPaidRevenue] = useState(false);

  const styles = {
    headerView: {
      padding: "30px 30px 0px 30px",
      backgroundColor: Colors.WHITE,
      borderBottom: `1px solid ${Colors.GRAY11}`,
    },
    contentView: {
      flex: 1,
      paddingBlock: 30,
      paddingInline: 30,
      backgroundColor: Colors.WHITE1,
    },
  };

  const {
    liveTotalGuests,
    liveTotalRequested,
    liveTotalRefunded,
    liveTotalLinkViews,
  } = useLiveUpdatingEventAnalytics({
    eventId: product.id,
    userId: product.createdBy,
  });

  const {
    liveCampaign: campaign,
    liveCampaignDetail,
    loaded: campaignLoaded,
  } = useLiveUpdatingCampaign({
    initialCampaignId: "",
    eventId: product.id,
    userId: accountData.uid,
  });

  const {
    isLoading: isLoadingTrackingLinks,
    isFinished: isFinishedTrackingLinks,
    fetchedLinkTrackers,
    loadSearchResultsThrottled,
    fetchMoreTrackingLinks,
    fetchTrackingLinks,
  } = useLoadTrackingLinksList({
    userId: product ? product.createdBy : accountData.uid,
    eventId: product.id,
    windowSize: 20,
  });

  const isCheckoutProduct = useMemo(
    () => isProductWithCheckout(product.eventType, product.accessLevel),
    [product.eventType, product.accessLevel]
  );

  useAsyncOnMount(async () => {
    const [hasPaidRevenue, { numUserEventLinkTrackers }] = await Promise.all([
      checkEventHasRevenue(product.id),
      API.tracking.getNumUserEventLinkTrackers({
        eventUserId: product.createdBy,
        eventId: product.id,
      }),
      fetchTrackingLinks(),
    ]);
    setHasPaidRevenue(hasPaidRevenue);
    setNumTotalLinkTrackers(numUserEventLinkTrackers);

    const userEventLinkTrackersRef = getUserEventLinkTrackersRef(
      product.createdBy,
      product.id
    );
    const query_ = query(
      userEventLinkTrackersRef,
      where("alias", "!=", DefaultLinkTrackerName.DEFAULT_EVENT_LINK_TRACKER)
    );
    const unsubscribe = onSnapshot(query_, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === "modified") {
          const { numUserEventLinkTrackers } =
            await API.tracking.getNumUserEventLinkTrackers({
              eventUserId: product.createdBy,
              eventId: product.id,
            });
          setNumTotalLinkTrackers(numUserEventLinkTrackers);
        }
      });
    });

    return () => unsubscribe();
  });

  const tabIndexes = useMemo(() => {
    let currentIndex = 0;

    const indexes = {
      content: currentIndex++,
      texts: undefined as number | undefined,
      analytics: undefined as number | undefined,
      forms: undefined as number | undefined,
      promos: undefined as number | undefined,
      settings: undefined as number | undefined,
    };

    if (isCheckoutProduct) {
      indexes.texts = currentIndex++;
    }

    indexes.analytics = currentIndex++;

    if (isCheckoutProduct) {
      indexes.forms = currentIndex++;
      indexes.promos = currentIndex++;
    }

    indexes.settings = currentIndex++;

    return indexes;
  }, [isCheckoutProduct]);

  if (
    isCheckoutProduct &&
    (!campaignLoaded || !campaign || !liveCampaignDetail)
  ) {
    return <LoadingScreen isLoadingPage={!campaignLoaded} />;
  }

  return (
    <CreatorModeWrapper>
      <div style={styles.headerView}>
        <EventDashboardHeader
          event={product}
          userTicket={userTicket}
          openShareModal={() => setIsShareModalOpen(true)}
        />
        <EventDashboardTabs
          dashboardValue={dashboardValue}
          setDashboardValue={setDashboardValue}
          viewTextsTab={isCheckoutProduct}
          viewAnalyticsTab={true}
          viewFormsTab={isCheckoutProduct}
          viewPromosTab={isCheckoutProduct}
          viewSettingsTab={true}
          eventType={product.eventType}
        />
      </div>
      <div style={styles.contentView}>
        <TabPanel value={dashboardValue} index={0}>
          {product.accessLevel === AccessLevel.CHECKOUT &&
          campaign &&
          liveCampaignDetail ? (
            <OrderListPanel
              product={product}
              userTicket={userTicket}
              dashboardValue={dashboardValue}
              totalGuests={liveTotalGuests}
              totalRequested={liveTotalRequested}
              totalRefunded={liveTotalRefunded}
              campaign={campaign}
              campaignDetail={liveCampaignDetail}
            />
          ) : product.eventType === EventType.POST ? (
            <FullPostContent
              post={product}
              creatorData={accountData}
              hardTheme={ThemeType.Light}
            />
          ) : (
            <FullProductContent
              product={product}
              creatorData={accountData}
              hardTheme={ThemeType.Light}
            />
          )}
        </TabPanel>
        {tabIndexes.texts !== undefined ? (
          <TabPanel value={dashboardValue} index={tabIndexes.texts}>
            <TextsPanel
              event={product}
              campaign={campaign}
              campaignDetail={liveCampaignDetail}
            />
          </TabPanel>
        ) : null}
        <TabPanel value={dashboardValue} index={tabIndexes.analytics ?? 0}>
          <AnalyticsPanel
            event={product}
            totalGuests={liveTotalGuests}
            totalLinkViews={liveTotalLinkViews}
            canMassText
            fetchedLinkTrackers={fetchedLinkTrackers}
            isFinishedTrackingLinks={isFinishedTrackingLinks}
            isLoadingTrackingLinks={isLoadingTrackingLinks}
            loadSearchResultsThrottled={loadSearchResultsThrottled}
            fetchMoreTrackingLinks={fetchMoreTrackingLinks}
            setTrackingLinkModalVisible={setTrackingLinkModalVisible}
            numTotalLinkTrackers={numTotalLinkTrackers}
            linkTrackerId={linkTrackerId}
            setLinkTrackerId={setLinkTrackerId}
            setTrackingLinkModalText={setTrackingLinkModalText}
            ticketAnalyticsData={{
              userTicket,
              totalScanned: 0,
              dashboardVisibility: 2,
              isPaymentCollectorRole: false,
            }}
          />
        </TabPanel>
        {tabIndexes.forms !== undefined ? (
          <TabPanel value={dashboardValue} index={tabIndexes.forms}>
            <FormsPanel event={product} dashboardVisibility={2} />
          </TabPanel>
        ) : null}
        {tabIndexes.promos !== undefined ? (
          <TabPanel value={dashboardValue} index={tabIndexes.promos}>
            <PromoCodePanel event={product} />
          </TabPanel>
        ) : null}
        <TabPanel value={dashboardValue} index={tabIndexes.settings ?? 0}>
          <SettingsPanel
            event={product}
            userTicket={userTicket}
            totalGuests={liveTotalGuests}
            hasPaidRevenue={hasPaidRevenue}
            dashboardVisibility={2}
            isHost={true}
          />
        </TabPanel>
      </div>
      {isShareModalOpen ? (
        <ShareEventModal
          event={product}
          userTicket={userTicket}
          revShare={false}
          hideTrackingLinks={true}
          closeShareModal={() => setIsShareModalOpen(false)}
          isTrackingLinksView={isTrackingLinksView}
          setIsTrackingLinksView={setIsTrackingLinksView}
          trackingLinkData={fetchedLinkTrackers}
          fetchMoreTrackingLinks={fetchMoreTrackingLinks}
          canLoadMoreTrackingLinks={
            !isFinishedTrackingLinks && !isLoadingTrackingLinks
          }
          setTrackingLinkModalVisible={setTrackingLinkModalVisible}
        />
      ) : null}
      {trackingLinkModalVisible ? (
        <TrackingLinkModal
          event={product}
          linkTrackerId={linkTrackerId}
          setLinkTrackerId={setLinkTrackerId}
          trackingLinkModalText={trackingLinkModalText}
          setTrackingLinkModalText={setTrackingLinkModalText}
          setTrackingLinkModalVisible={setTrackingLinkModalVisible}
          openShareModal={() => setIsShareModalOpen(true)}
          setIsTrackingLinksView={setIsTrackingLinksView}
        />
      ) : null}
    </CreatorModeWrapper>
  );
};

export default memo(ProductDashboard);
